import React from "react";
import {
  Card,
  CardHeader,
  CardTitle,
  CardText
} from "material-ui/Card";
import Chip from 'material-ui/Chip';
import EliminatedIcon from "material-ui/svg-icons/action/highlight-off";
import CheckmarkIcon from "material-ui/svg-icons/navigation/check";
import { List, ListItem } from "material-ui/List";
import caveatColor from "../caveatColor";
import CaveatIcon from "material-ui/svg-icons/alert/error-outline";
import CondensedCaveatAvatars from "../components/resultItems/CondensedCaveatAvatars";
import ColorCodedCriteriaAvatars from "../components/resultItems/ColorCodedCriteriaAvatars";
import { getCriteriaColor, criteriaColors } from "../reducer/response";
import {assessmentTiers} from "../components/resultItems/AssessmentTier";

const hasText = text => text && text.replace(/<\/?[^>]+(>|$)/g, "").length > 1;
const onTextClick = (e) => {
  if (e.target.href) {
    window.open(e.target.href, '_blank');
    e.preventDefault();
  }
}

const OptionalDescription = ({ content, title }) => {
  if (hasText(content)) {
    return (
      <div>
        {title ? (
          <CardTitle style={{ paddingBottom: 0 }} subtitle={title} />
        ) : null}
        <CardText style={{ paddingTop: 0 }} onClick={onTextClick}>
          <div dangerouslySetInnerHTML={{ __html: content }} />
        </CardText>
      </div>
    );
  } else {
    return null;
  }
};

const CaveatListItem = ({ isStatic, question, score, title, answer }) => {
  const color = caveatColor(isStatic ? 10 : score);
  var nestedItems;
  nestedItems = [
    <ListItem
      key={"blah" + (question ? question.id : "static")}
      primaryText={title}
      leftIcon={<CaveatIcon />}
      style={{
        backgroundColor: color.background,
        borderLeft: `4px solid ${color.primary}`,
        marginTop: -8,
        marginLeft: 0
      }}
      disabled={true}
    />
  ];
  return (
    <ListItem
      key={`question-${isStatic ? "static" : question.id}-caveat`}
      primaryText={isStatic ? "Static Caveat" : question.title.replace(/<.*?>/gm, '')}
      secondaryText={answer || "This caveat always applies to this option"}
      style={{
        backgroundColor: color.background,
        borderLeft: `4px solid ${color.primary}`
      }}
      nestedItems={nestedItems}
      autoGenerateNestedIndicator={false}
      open={true}
      disabled={true}
    />
  );
};

const CaveatList = props => {
  if (props.caveats.length === 0) {
    return null;
  }
  let title = props.title;
  if (typeof props.title === "function") {
    title = props.title(props.caveats.length);
  }
  if (props.caveats && props.caveats.length) {
    return (
      <div>
        <Card
          style={{ width: "85%", margin: "0 auto" }}
          initiallyExpanded={props.initiallyExpanded}
        >
          <CardHeader
            title={title}
            actAsExpander={true}
            showExpandableButton={true}
            open={true}
            style={{ userSelect: "none" }}
            avatar={
              <CondensedCaveatAvatars
                style={{
                  position: "absolute",
                  right: 48,
                  top: 8,
                  width: "auto",
                  minWidth: "auto",
                  margin: 0,
                  padding: 0
                }}
                caveats={props.caveats}
              />
            }
          />
          <CardText expandable={true}>
            <List>
              {props.caveats
                .sort((a, b) => a.score - b.score)
                .map((caveat, i) => {
                  const question = props.questionLookup[caveat.question];
                  const answer = question
                    ? props.answerTextLookup[question.id]
                    : null;
                  return (
                    <CaveatListItem
                      key={props.optionID + "-cli-" + answer +  caveat.question + caveat.title}
                      {...caveat}
                      question={question}
                      isStatic={!question}
                      answer={answer}
                    />
                  );
                })}
            </List>
          </CardText>
        </Card>
        <br />
      </div>
    );
  } else {
    return null;
  }
};

const OrCriteriaListItem = ({
  question,
  answer,
  criteria,
  hideColorCoding,
  allQuestions,
  criterion
}) => {
  allQuestions.sort((a, b) => {
    return criteriaColors.indexOf(getCriteriaColor(b.answer, b.answers, criteria)) - criteriaColors.indexOf(getCriteriaColor(a.answer, a.answers, criteria))
  });
  question = allQuestions.shift();
  return (
    <Card>
    <CardHeader
      style={{userSelect: 'none'}}
      title="Multi-part criterion"
      subtitle={`This criterion can be satisfied by ${allQuestions.length + 1} different questions.`}
      actAsExpander={true}
      showExpandableButton={true}
    />
    <CriteriaListItem
      question={question}
      answer={question.fullAnswer}
      criteria={criteria}
      hideColorCoding={hideColorCoding}
    />
    <div expandable={true}>
      {
        allQuestions.map((question) => (
          <CriteriaListItem
            key={question.id}
            question={question}
            answer={question.fullAnswer}
            criteria={question.criterion}
            hideColorCoding={hideColorCoding}
          />
        ))
      }
    </div>
  </Card>
  );
};

const answerListItemStyle = {
  padding: 5,
};

const AnswerListItem = ({ title, isThreshold, color, isChoice }) => (
  <div
    style={{
      ...answerListItemStyle,
      // borderTop: isThreshold ? "1px dashed black" : "none",
      borderLeft: `5px solid ${color}`,
      background: isChoice ? color : color.replace('rgb', 'rgba').replace(")", ",0.05)"),
      boxShadow: isChoice ? "2px 2px 5px rgba(0,0,0,0.4)" : "none",
      color: isChoice ? (color === 'rgb(231,214,91)' ? "black" : "white") : "black"
    }}
  >
    {title}
  </div>
);

const CriteriaListItem = ({ question, answer, criteria, hideColorCoding }) => {
  const answers = question.answers.map(a => ({
    ...a,
    isChoice: a.id === answer.id,
    color: getCriteriaColor(a.id, question.answers, criteria, hideColorCoding)
  }));
  for (const a of answers) {
    if (a.color === criteriaColors[0]) {
      // red
      a.isThreshold = true;
    }
  }

  return (
    <div style={{paddingBottom: 22}}>
      <CardHeader title={question.title.replace(/<.*?>/gm, '')} />
      <div style={{
        marginLeft: 15,
        border: '1px solid #efefef'
      }}>
        {answers.map(a => <AnswerListItem key={a.id} {...a} />)}
      </div>
    </div>
  );
};

const CriteriaList = ({
  optionID,
  eliminated,
  criteria,
  title,
  avatar,
  initiallyExpanded,
  hideColorCoding,
  questionLookup
}) => {
  if (!criteria || criteria.length === 0) {
    return null;
  } else {
    var nCriteria = 0;
    var orCriteriaRead = {};
    for (var c of criteria) {
      if (c.or) {
        if (!orCriteriaRead[c.or]) {
          orCriteriaRead[c.or] = true;
          nCriteria += 1;
        }
      } else {
        nCriteria += 1;
      }
    }

    if (typeof title === "function") {
      title = title(nCriteria);
    }

    var simpleQuestions = criteria.map((question) => {
      let answer = (question.answers || []).find(
        a => a.id === question.answer
      );
      var criterion = [];
      if (question.or) {
        const orQuestion = questionLookup[question.or];
        if (orQuestion) {
          criterion = orQuestion.domainLogic.criteria[optionID];
        }
      } else {
        criterion = question.domainLogic.criteria[optionID];
      }
      return {
        ...question,
        criterion: criterion,
        fullAnswer: answer
      }
    });

    const orQuestions = simpleQuestions.filter((q) => !!q.or).reduce((arr, question) => {
      const existing = arr.find((q) => q.question.or === question.or);
      if (existing) {
        existing.otherQuestions.push(question);
      } else {
        arr.push({
          question: question,
          otherQuestions: []
        });
      }
      return arr;
    }, []);

    simpleQuestions = simpleQuestions.filter((q) => !q.or);
    return (
      <div>
        <Card
          style={{ width: "85%", margin: "0 auto" }}
          initiallyExpanded={initiallyExpanded}
        >
          <CardHeader
            title={title}
            actAsExpander={true}
            showExpandableButton={true}
            style={{ userSelect: "none" }}
            avatar={
              <ColorCodedCriteriaAvatars
                hideColorCoding={hideColorCoding}
                metCriteria={criteria}
                eliminated={eliminated}
                eliminatedBy={[]}
                style={{
                  position: "absolute",
                  right: 48,
                  top: 12,
                  width: "auto",
                  minWidth: "auto",
                  margin: 0,
                  padding: 0
                }}
                innerStyle={{
                  width: "auto"
                }}
              />
            }
          />
          <CardText expandable={true} style={{ marginBottom: 10 }}>
            <List>
              {simpleQuestions.map(question => (
                <CriteriaListItem
                  key={question.id + "-criteria"}
                  question={question}
                  answer={question.fullAnswer}
                  hideColorCoding={hideColorCoding}
                  criteria={question.criterion}
                />
              ))}
              {
                orQuestions.map(({question, otherQuestions}) => (
                  <OrCriteriaListItem
                    key={question.id + "-criteria"}
                    question={question}
                    answer={question.fullAnswer}
                    hideColorCoding={hideColorCoding}
                    criteria={question.criterion}
                    allQuestions={[question, ...otherQuestions]}
                  />
                ))
              }
            </List>
          </CardText>
        </Card>
        <br />
      </div>
    );
  }
};

class EvaluatedOptionCard extends React.Component {
  render() {
    let {
      title,
      inputCategory,
      category,
      assessmentTier,
      eliminated,
      description,
      eliminatedBy,
      metCriteria,
      caveats,
      questionLookup,
      assessmentOutput,
      contacts,
      references,
      hideResults,
      section,
      tags
    } = this.props;
    const optionID = this.props.id;
    let avatar = eliminated ? (
      <EliminatedIcon color="red" />
    ) : (
      <CheckmarkIcon color="green" />
    );
    if (hideResults) {
      avatar = null;
      eliminated = false;
      caveats = [];
    }

    let inputCatTitle = inputCategory ? inputCategory.title : "";
    let assessmentTierTitle = assessmentTier ? assessmentTiers.find(tier => tier.id === assessmentTier).title : "";
    let subtitleText = (section === 'assessment') ? (<div>Assessment Output: {category.title}<br/>Assessment Category: {inputCatTitle}<br/>Assessment 'Tier': {assessmentTierTitle}</div>) : "Category: " + category.title

    return (
      <Card style={{ maxWidth: 800, margin: "10px auto" }} initiallyExpanded={!hideResults}>
        <CardHeader
          avatar={avatar}
          title={title}
          subtitle={subtitleText}
          actAsExpander={hideResults}
          showExpandableButton={hideResults}
        />
        <CardText expandable={hideResults}>
          <OptionalDescription content={description} />
          { tags && tags.length ? (
            <div>
              <CardTitle style={{ paddingBottom: 0 }} subtitle="Tags" />
              <CardText style={{ paddingTop: 10 }}>
                {
                  tags.map((t) => <Chip key={this.props.id + t.id}>{t.label}</Chip>)
                }
              </CardText>
            </div>

          ) : null}
          <OptionalDescription
            title="Assessment Output"
            content={assessmentOutput}
          />
          <OptionalDescription title="Contacts" content={contacts} />
          <OptionalDescription title="References" content={references} />
          {!hideResults && (
            <div>
              <CriteriaList
                criteria={eliminatedBy}
                eliminated={eliminated}
                avatar={<EliminatedIcon color="red" />}
                title={n => `Eliminated for failing ${n} criteria`}
                hideColorCoding={section !== "assessment"}
                optionID={optionID}
                questionLookup={questionLookup}
              />
              <CriteriaList
                optionID={optionID}
                criteria={metCriteria}
                title={n => `Met ${n} criteria`}
                avatar={<CheckmarkIcon color="green" />}
                hideColorCoding={section !== "assessment"}
                questionLookup={questionLookup}
              />
              <CaveatList
                optionID={optionID}
                answerTextLookup={this.props.answerTextLookup}
                caveats={caveats.filter(c => c.score <= 0 && !c.static)}
                questionLookup={this.props.questionLookup}
                initiallyExpanded={false}
                title={n => `${n} caveat${n === 1 ? "" : "s"}`}
              />
              <CaveatList
                optionID={optionID}
                answerTextLookup={this.props.answerTextLookup}
                caveats={caveats.filter(c => c.score > 0 && !c.static)}
                questionLookup={this.props.questionLookup}
                initiallyExpanded={false}
                title={n => `${n} positive attribute${n === 1 ? "" : "s"}`}
              />
              <CaveatList
                optionID={optionID}
                answerTextLookup={this.props.answerTextLookup}
                caveats={caveats.filter(c => c.static)}
                questionLookup={this.props.questionLookup}
                initiallyExpanded={false}
                title={n => `${n} static caveat${n === 1 ? "" : "s"}`}
              />
            </div>
          )}
        </CardText>
      </Card>
    );
  }
}

export default EvaluatedOptionCard;
